import { Button, Input, Space, TableProps, Tooltip } from "antd";
import { useEffect } from "react";
import AddIcon from "../../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import ConfirmModal from "../../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../../components/ui/CustomTable";
import {
  AllLeaveAllocationParams,
  LeaveAllocation as TLeaveAllocation,
} from "../../../../../features/organization/contractual/LeaveManagement/leaveManagement.type";
import {
  useDeleteLeaveAllocationMutation,
  useGetAllLeaveAllocationQuery,
} from "../../../../../features/organization/contractual/LeaveManagement/leaveManagementApi";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import useTable from "../../../../../hooks/useTable";
import { toast } from "../../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../../utils/number";
import { valueOrNa } from "../../../../../utils/string";
import { addKeyInArray } from "../../../../../utils/utils";
import ManageLeaveType from "./ManageLeaveType";

export default function LeaveAllocation() {
  const { open, handleOpen, handleClose } = useModal<
    TLeaveAllocation,
    "CONFIRM"
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handlePagination,
  } = useTable<AllLeaveAllocationParams>();

  const { data, isLoading, isFetching } = useGetAllLeaveAllocationQuery({
    params: { ...filter, search: keyword },
  });

  const [
    deleteLeaveAllocation,
    {
      isError: isDeleteError,
      error: deleteError,
      isLoading: deleting,
      isSuccess: deleted,
    },
  ] = useDeleteLeaveAllocationMutation();

  useEffect(() => {
    if (deleted) {
      handleClose();
      toast("success", "Leave type deleted successfully.");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  useError(isDeleteError, deleteError, "KPI Group delete failed.");

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Leave Type",
      dataIndex: "leave_name",
      render: (val) => valueOrNa(val),
      width: "33%",
    },
    {
      title: "Total Days",
      dataIndex: "total_days",
      align: "center",
      render: (val) => valueOrNa(val),
      width: "33%",
    },
    {
      title: "Action",
      align: "end",
      render: (record) => (
        <Space align="center">
          <Tooltip title="Edit">
            <Button
              className="action-edit"
              size="small"
              icon={<EditIcon />}
              onClick={() => handleOpen({ type: "UPDATE", data: record })}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              onClick={() =>
                handleOpen({
                  type: "CONFIRM",
                  data: {
                    id: record.id,
                  } as TLeaveAllocation,
                })
              }
              className="action-delete"
              size="small"
              icon={<DeleteIcon />}
            />
          </Tooltip>
        </Space>
      ),
      width: "33%",
    },
  ];

  return (
    <>
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total || 0}
        dataSource={addKeyInArray(data?.data?.leaveAllocations || [])}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <Button
              color="primary"
              type="primary"
              onClick={() =>
                handleOpen({ type: "ADD", data: {} as TLeaveAllocation })
              }
            >
              <AddIcon /> New Leave Type
            </Button>
          </>
        }
      />
      <ConfirmModal
        width={450}
        open={open?.open && open.type === "CONFIRM"}
        okLoading={deleting}
        okText="Yes, Delete"
        title={`Delete`}
        handleClose={() => handleClose()}
        handleOk={() => deleteLeaveAllocation({ id: open.data?.id })}
        subTitle={`Are you sure  you want to delete the selected leave type?`}
      />
      <ManageLeaveType
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && (open.type === "ADD" || open.type === "UPDATE")}
      />
    </>
  );
}
